import { ChangeEvent, useState, useRef, useEffect } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  OutlinedInput,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocationSearchInput from './LocationSearchInput';

const LocationComponent = ({ field, form }: any) => {
  const { t } = useTranslation();
  const [_latitude, setLatitude] = useState<number | null>(null);
  const [_longitude, setLongitude] = useState<number | null>(null);

  const [address, setAddress] = useState(field.value);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const initializedRef = useRef(false);

  const createMarker = (lat: number, lng: number) => {
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    const marker = new google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current!,
      draggable: true,
    });

    marker.addListener('dragend', () => {
      const position = marker.getPosition();
      if (position) {
        handleMarkerDrag(position.lat(), position.lng());
      }
    });

    markerRef.current = marker;
  };

  const updateFormValues = (lat: number, lng: number) => {
    form.setFieldValue('latitude', lat.toString());
    form.setFieldValue('longitude', lng.toString());
    setLatitude(lat);
    setLongitude(lng);
  };

  const updateAddressFromCoords = (lat: number, lng: number) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const formattedAddress = results[0].formatted_address;
        form.setFieldValue(field.name, formattedAddress);
        setAddress(formattedAddress);
      }
    });
  };

  const handleMapClick = (lat: number, lng: number) => {
    createMarker(lat, lng);
    updateFormValues(lat, lng);
    updateAddressFromCoords(lat, lng);
  };

  const handleMarkerDrag = (lat: number, lng: number) => {
    updateFormValues(lat, lng);
    updateAddressFromCoords(lat, lng);
  };

  const initializeMap = () => {
    if (initializedRef.current || !mapRef.current || !window.google) return;

    const map = new google.maps.Map(mapRef.current, {
      center: { lat: 27.0, lng: 69.0 }, // A point near the India-Pakistan border
      zoom: 5, // Slightly zoomed out to cover both countries
      streetViewControl: false,
      mapTypeControl: true,
      fullscreenControl: true,
    });

    map.addListener('click', (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat && lng) {
        handleMapClick(lat, lng);
      }
    });

    mapInstanceRef.current = map;
    initializedRef.current = true;
  };

  const handleSearchLocation = ({
    latLng,
    city,
    state,
    country,
    formattedAddress
  }: {
    latLng: { lat: number; lng: number };
    city: string;
    state: string;
    country: string;
    formattedAddress?:string
  }) => {
    const { lat, lng } = latLng;
    const locationString = formattedAddress?formattedAddress:`${city}, ${state}, ${country}`;
    updateFormValues(lat, lng);
    form.setFieldValue(field.name, locationString);
    setAddress(locationString);

    // Wait until map is initialized
    const tryUpdateMap = () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter({ lat, lng });
        mapInstanceRef.current.setZoom(15);
        createMarker(lat, lng);
      } else {
        // Retry after short delay if map not ready
        setTimeout(tryUpdateMap, 100);
      }
    };

    tryUpdateMap();
  };


  useEffect(() => {
    initializeMap();
  }, []);


  useEffect(() => {
    const loadInitialAddress = () => {
      if (!field.value || !window.google || !mapInstanceRef.current) return;

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: field.value }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const location = results[0].geometry.location;
          const lat = location.lat();
          const lng = location.lng();

          updateFormValues(lat, lng);
          mapInstanceRef.current!.setCenter({ lat, lng });
          mapInstanceRef.current!.setZoom(15);
          createMarker(lat, lng);
        }
      });
    };

    if (initializedRef.current) {
      loadInitialAddress();
    } else {
      const waitForMap = setInterval(() => {
        if (initializedRef.current) {
          loadInitialAddress();
          clearInterval(waitForMap);
        }
      }, 100);
    }
  }, [field.value]);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl
        error={form.touched[field.name] && Boolean(form.errors[field.name])}
        fullWidth
      >
        <LocationSearchInput
          handlesearchLocation={handleSearchLocation}
          address={address}
          setAddress={(val) => {
            setAddress(val);
            form.setFieldValue(field.name, val);
            form.setFieldTouched(field.name, true, false);
          }}
        />
        {form.touched[field.name] && form.errors[field.name] && (
          <FormHelperText>{form.errors[field.name]}</FormHelperText>
        )}
      </FormControl>

      <Paper variant="outlined" sx={{ height: 400, width: '100%' }}>
        <Box ref={mapRef} sx={{ height: '100%', width: '100%' }} />
      </Paper>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {t('storeManagement.form.latitude')}
        </Typography>
        <FormControl fullWidth>
          <OutlinedInput
            value={form.values.latitude}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              form.setFieldValue('latitude', e.target.value)
            }
            disabled
            placeholder={t('storeManagement.form.enterLatitude')}
            sx={{ backgroundColor: '#f5f5f5', height: '35px', width: '350px' }}
          />
        </FormControl>

        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {t('storeManagement.form.longitude')}
        </Typography>
        <FormControl fullWidth>
          <OutlinedInput
            value={form.values.longitude}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              form.setFieldValue('longitude', e.target.value)
            }
            disabled
            placeholder={t('storeManagement.form.enterLongitude')}
            sx={{ backgroundColor: '#f5f5f5', height: '35px', width: '350px' }}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

export default LocationComponent;
